import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Navbar.css';
import logo from '../../Assets/logo2.png';
import { FaSearch, FaBars, FaTimes, FaPhone, FaEnvelope } from 'react-icons/fa';
import { useSiteContent } from '../../context/SiteContentContext';
import { useSiteSearch } from '../../hooks/useSiteSearch';
import {
  buildSearchResultsNavigation,
  getSearchResultMeta,
  searchSiteDocuments,
} from '../../utils/siteSearch';

const DEFAULT_PRODUCT_CATEGORIES = [
  { name: 'Tube Fittings' },
  { name: 'Valves' },
  { name: 'Double Block & Bleed Valves' },
  { name: 'Manifolds' },
  { name: 'Flange Adaptors' },
  { name: 'Regulators' },
  { name: 'Filters' },
  { name: 'Swivel Gauge Adaptor' },
  { name: 'Air Manifolds' },
  { name: 'Condensate Pot' },
  { name: 'Sampling System' },
  { name: 'Pressure Gauge' },
  { name: 'Temperature Gauge' },
  { name: 'Thermowells' },
  { name: 'Accessories' },
  { name: 'Others' },
];

const Navbar = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchRef = useRef(null);
  const productDropdownRef = useRef(null);
  const productDropdownCloseTimerRef = useRef(null);
  const { siteContent } = useSiteContent();
  const trimmedSearch = search.trim();
  const isSearchActive = isSearchExpanded || Boolean(trimmedSearch);
  const { searchDocuments, isSearchIndexLoading } = useSiteSearch({ enabled: isSearchActive });

  const productCategories =
    siteContent?.product_dropdown_items &&
    Array.isArray(siteContent.product_dropdown_items) &&
    siteContent.product_dropdown_items.length > 0
      ? siteContent.product_dropdown_items.map((name) => ({ name }))
      : DEFAULT_PRODUCT_CATEGORIES;
  const filteredSuggestions = trimmedSearch
    ? searchSiteDocuments(trimmedSearch, searchDocuments, { limit: 6 })
    : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setIsSearchExpanded(false);
      }

      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearProductDropdownCloseTimer = () => {
    if (productDropdownCloseTimerRef.current) {
      clearTimeout(productDropdownCloseTimerRef.current);
      productDropdownCloseTimerRef.current = null;
    }
  };

  const scrollPageToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  };

  const closeSearch = ({ clearInput = false } = {}) => {
    setShowSuggestions(false);
    setIsSearchExpanded(false);

    if (clearInput) {
      setSearch('');
    }
  };

  const navigateFromSearch = (navigation, { clearInput = true } = {}) => {
    if (!navigation) {
      return;
    }

    scrollPageToTop();
    navigate(navigation);
    closeSearch({ clearInput });
    setIsHamburgerOpen(false);
  };

  const handleProductDropdownMouseEnter = () => {
    clearProductDropdownCloseTimer();
    setShowProductDropdown(true);
  };

  const handleProductDropdownMouseLeave = () => {
    productDropdownCloseTimerRef.current = setTimeout(() => {
      setShowProductDropdown(false);
      productDropdownCloseTimerRef.current = null;
    }, 200);
  };

  const handleProductClick = (category) => {
    scrollPageToTop();
    navigate(`/products?category=${encodeURIComponent(category)}`);
    setShowProductDropdown(false);
    setIsHamburgerOpen(false);
  };

  const handleProductsLinkClick = (event) => {
    event.preventDefault();
    scrollPageToTop();
    navigate('/products');
    setShowProductDropdown(false);
    setIsHamburgerOpen(false);
  };

  const handleHomeClick = (event) => {
    event.preventDefault();
    navigate('/');
    setIsHamburgerOpen(false);
  };

  const handleCertificatesClick = (event) => {
    event.preventDefault();
    navigate('/certificates');
    setIsHamburgerOpen(false);
  };

  const handleCatalogClick = (event) => {
    event.preventDefault();
    navigate('/catalog');
    setIsHamburgerOpen(false);
  };

  const handleNewsClick = (event) => {
    event.preventDefault();
    navigate('/news-blogs');
    setIsHamburgerOpen(false);
  };

  const handleContactClick = (event) => {
    event.preventDefault();
    navigate('/contact');
    setIsHamburgerOpen(false);
  };

  const handleMobileProductClick = (event) => {
    event.preventDefault();
    scrollPageToTop();
    navigate('/products');
    setIsHamburgerOpen(false);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    if (!trimmedSearch) {
      return;
    }

    navigateFromSearch(buildSearchResultsNavigation(trimmedSearch));
  };

  const handleSuggestionClick = (suggestion) => {
    navigateFromSearch(suggestion.navigation);
  };

  const handleViewAllResults = () => {
    if (!trimmedSearch) {
      return;
    }

    navigateFromSearch(buildSearchResultsNavigation(trimmedSearch));
  };

  const handleSearchIconClick = () => {
    const nextExpandedState = !isSearchExpanded;
    setIsSearchExpanded(nextExpandedState);

    if (!nextExpandedState) {
      setShowSuggestions(false);
      return;
    }

    setTimeout(() => {
      const searchInput = searchRef.current?.querySelector('input');

      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  };

  return (
    <header className="navbar-wrapper">
      <div className="top-header-bar">
        <a href="tel:+919167633933" className="top-header-link">
          <FaPhone /> +91 9167633933
        </a>
        <a href="mailto:sales@hkinds.com" className="top-header-link">
          <FaEnvelope /> sales@hkinds.com
        </a>
      </div>

      <nav className="navbar">
        <a
          href="/"
          className="navbar-logo animated-logo"
          onClick={handleHomeClick}
          aria-label="Go to homepage"
        >
          <img
            src={logo}
            alt="Hari Krishna Industries logo"
            fetchPriority="high"
            decoding="async"
          />
        </a>

        <ul className="navbar-links">
          <li>
            <a href="/" onClick={handleHomeClick}>HOME</a>
          </li>
          <li
            className="products-dropdown-container"
            ref={productDropdownRef}
            onMouseEnter={handleProductDropdownMouseEnter}
            onMouseLeave={handleProductDropdownMouseLeave}
          >
            <a href="/products" onClick={handleProductsLinkClick}>PRODUCTS</a>
            <div
              className={`products-dropdown ${showProductDropdown ? 'products-dropdown-open' : 'products-dropdown-closed'}`}
              onMouseEnter={handleProductDropdownMouseEnter}
              onMouseLeave={handleProductDropdownMouseLeave}
            >
              {showProductDropdown && (
                <div className="dropdown-columns">
                  <div className="dropdown-column">
                    {productCategories.slice(0, 8).map((category, index) => (
                      <div key={index} className="dropdown-item">
                        <span
                          className="category-name"
                          onClick={() => handleProductClick(category.name)}
                        >
                          {category.name}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="dropdown-column">
                    {productCategories.slice(8).map((category, index) => (
                      <div key={index + 8} className="dropdown-item">
                        <span
                          className="category-name"
                          onClick={() => handleProductClick(category.name)}
                        >
                          {category.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </li>
          <li>
            <a href="/certificates" onClick={handleCertificatesClick}>CERTIFICATES</a>
          </li>
          <li>
            <a href="/catalog" onClick={handleCatalogClick}>CATALOG</a>
          </li>
          <li>
            <a href="/news-blogs" onClick={handleNewsClick}>NEWS & BLOGS</a>
          </li>
          <li>
            <a href="/contact" onClick={handleContactClick}>CONTACT US</a>
          </li>
        </ul>

        <div className="navbar-search" ref={searchRef}>
          <div className={`search-container ${isSearchExpanded ? 'expanded' : ''}`}>
            <button
              type="button"
              className="search-icon-btn"
              onClick={handleSearchIconClick}
              aria-label="Toggle search"
            >
              <FaSearch />
            </button>

            <form onSubmit={handleSearchSubmit} className="search-form">
              <input
                type="text"
                placeholder="Search any page, product, news, or content..."
                value={search}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setSearch(nextValue);
                  setShowSuggestions(Boolean(nextValue.trim()));
                }}
                onFocus={() => setShowSuggestions(Boolean(trimmedSearch))}
              />

              {showSuggestions && trimmedSearch && (
                <div className="search-suggestions-panel">
                  {filteredSuggestions.length > 0 ? (
                    <ul className="suggestions-list">
                      {filteredSuggestions.map((suggestion) => (
                        <li key={suggestion.id} onClick={() => handleSuggestionClick(suggestion)}>
                          <div className="suggestion-copy">
                            <span className="suggestion-label">{suggestion.title}</span>
                            <span className="suggestion-meta">{getSearchResultMeta(suggestion)}</span>
                            {suggestion.snippet && (
                              <span className="suggestion-snippet">{suggestion.snippet}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="search-suggestions-state">
                      {isSearchIndexLoading
                        ? 'Searching site content...'
                        : 'No direct matches yet. View full results for more options.'}
                    </div>
                  )}

                  <button
                    type="button"
                    className="search-view-all-btn"
                    onClick={handleViewAllResults}
                  >
                    View all results for "{trimmedSearch}"
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="hamburger-container">
          <button
            className="hamburger-btn"
            onClick={() => setIsHamburgerOpen(!isHamburgerOpen)}
            aria-label="Toggle menu"
          >
            {isHamburgerOpen ? <FaTimes /> : <FaBars />}
          </button>

          <div className={`hamburger-menu ${isHamburgerOpen ? 'open' : ''}`}>
            <ul>
              <li>
                <a href="/" onClick={handleHomeClick}>HOME</a>
              </li>
              <li>
                <a href="/products" onClick={handleMobileProductClick}>PRODUCTS</a>
              </li>
              <li>
                <a href="/certificates" onClick={handleCertificatesClick}>CERTIFICATES</a>
              </li>
              <li>
                <a href="/catalog" onClick={handleCatalogClick}>CATALOG</a>
              </li>
              <li>
                <a href="/news-blogs" onClick={handleNewsClick}>NEWS & BLOGS</a>
              </li>
              <li>
                <a href="/contact" onClick={handleContactClick}>CONTACT US</a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
